import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { NavCardList, type NavCardItem } from '@/components/shared/NavCardList';
import { usePlugins } from '@/hooks/config/usePlugins';
import { mergeNavAndPluginCards } from '@/components/layout/NavBar/nav-items';
import type { NavItem } from '@/components/layout/NavBar/nav-types';
import { useActiveOrgSlug } from '@/lib/useOrgRoutes';
import { withOrgSlug } from '@/lib/routes';

interface ResourceGroupOverviewPageProps {
  icon: ReactNode;
  title: string;
  description: string;
  coreNavItems: readonly NavItem[];
  group: string;
  bottomRoutes?: readonly string[];
  docs?: string;
}

export function ResourceGroupOverviewPage({
  icon,
  title,
  description,
  coreNavItems,
  group,
  bottomRoutes,
  docs,
}: ResourceGroupOverviewPageProps) {
  const { overviewCards, navItems: pluginNavItems } = usePlugins();
  const slug = useActiveOrgSlug();
  const items = useMemo<NavCardItem[]>(() => {
    const scopedPluginNav = pluginNavItems
      .filter((i) => i.group === group)
      .map((i) => ({ ...i, to: i.rootLevel ? i.to : withOrgSlug(slug, i.to) }));
    const scopedOverviewCards = overviewCards.map((c) => ({ ...c, to: withOrgSlug(slug, c.to) }));
    return mergeNavAndPluginCards(
      coreNavItems,
      scopedOverviewCards,
      scopedPluginNav,
      group,
      bottomRoutes,
    );
  }, [coreNavItems, overviewCards, pluginNavItems, group, bottomRoutes, slug]);

  return (
    <PageLayout maxWidth="4xl">
      <PageHeader
        iconVariant="box"
        icon={icon}
        title={title}
        description={description}
        docs={docs}
      />
      <NavCardList items={items} />
    </PageLayout>
  );
}
